import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Button,
  Pagination,
  Snackbar,
  Alert,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Toolbar,
  Stack,
  Chip,
  Divider,
  useMediaQuery,
  InputAdornment,            
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Delete,
  Edit,
  Add,
  Visibility,
  Link as LinkIcon,
  SwapHoriz,
  Search,                    
  Lock,
  LockOpen,
  BatteryFull,
  BatteryAlert,
  FiberManualRecord,
  Business,
} from "@mui/icons-material";
import DevicesOtherOutlinedIcon from "@mui/icons-material/DevicesOtherOutlined";
import styles from "./ManageListings.styles";

import { useListingTransfer } from "../../hooks/useListingTransfer";
import { useAuth } from "../../hooks/useAuth";
import { getAuth } from "firebase/auth";
import app from "../../firebase";
import TransferPaymentModal from "./TransferPaymentModal";
import PropertyDetailView from "./PropertyDetailView";
import AccessCodeManager from "./AccessCodeManager";
import GenerateVendorLinkDialog from "../maintenance-vendor/GenerateVendorLinkDialog";
import { getLocksByProperty } from "../../services/PropertyLockService";

import useUserProfile from "../../hooks/useUserProfile";
import { useUserRole } from "../../helpers/userRole";

import {
  TRANSFER_BASE,
  TRANSFER_EXTRA,
  priceForCount,
  isValidEmail,
  shouldSkipPayment,
} from "../../helpers/transfers";
import FindMeATenantButton from "./FindMeATenantButton";
import NewSmartPackageFlow from "./NewSmartPackageFlow";

const ManageListings = ({
  listings,
  loading,
  error,
  handleCreateListing,
  handleEditListing,
  handleDeleteListing,
  handleViewListing,
  onTransfer = (email, ids) =>
    console.log("Transfer requested ->", { email, ids }),
}) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const { currentUser } = useAuth(); 
  const payerEmail = currentUser?.email || "";
  const uid = currentUser?.uid || null;
  const { profileData } = useUserProfile(uid);
  const ownerName = profileData?.fullName || payerEmail || "";
  // property transfer
  const [transferTypeOpen, setTransferTypeOpen] = useState(false);
  const [managerAccessOpen, setManagerAccessOpen] = useState(false);
  const [managerEmail, setManagerEmail] = useState("");
  const [managerEmailError, setManagerEmailError] = useState("");
  const [managerAccessLoading, setManagerAccessLoading] = useState(false);
  const [pendingManagerIds, setPendingManagerIds] = useState([]);


  // manager access
  const [managersDialogOpen, setManagersDialogOpen] = useState(false);
  const [selectedListingManagers, setSelectedListingManagers] = useState([]);
  const [managersListingId, setManagersListingId] = useState(null);
  const [managersLoading, setManagersLoading] = useState(false);
  const [revokingManagerId, setRevokingManagerId] = useState(null);
  const [managersCountMap, setManagersCountMap] = useState({});

  // 🔽 Centralized role/permissions/title
  const { dashboardTitle, isRoleReady } = useUserRole(currentUser);

  const [currentPage, setCurrentPage] = useState(1);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const itemsPerPage = 10;

  // Access code generation state
  const [accessCodeDialogOpen, setAccessCodeDialogOpen] = useState(false);
  const [selectedPropertyForAccessCode, setSelectedPropertyForAccessCode] = useState(null);
  const [selectedLockIdForAccessCode, setSelectedLockIdForAccessCode] = useState(null);

  // Vendor link generation state
  const [vendorLinkDialogOpen, setVendorLinkDialogOpen] = useState(false);
  const [selectedPropertyForVendorLink, setSelectedPropertyForVendorLink] = useState(null);

  // Property locks state (for mobile view)
  const [propertyLocksMap, setPropertyLocksMap] = useState({});
  const [locksLoadingMap, setLocksLoadingMap] = useState({});

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const openSnack = (message, severity = "success") =>
    setSnack({ open: true, message, severity });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [transferOpen, setTransferOpen] = useState(false);
  const [pendingIds, setPendingIds] = useState([]); 
  const [transferEmail, setTransferEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  // 🔍 Search state
  const [searchQuery, setSearchQuery] = useState("");

  const {
    transferListings,
    loading: xferLoading,
    error: xferError,
  } = useListingTransfer({
    listingCollection: "listings",
    ownerField: "ownerUid",
  });


  useEffect(() => {
    if (xferError) openSnack(xferError.message || "Transfer failed", "error");
  }, [xferError]);

  const safeListings = useMemo(() => Array.isArray(listings) ? listings : [], [listings]);

  // 🔍 Filtered listings based on search
  const filteredListings = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return safeListings;

    return safeListings.filter((listing) => {
      const id = String(listing.propertyId || "").toLowerCase();
      const address = (listing.address || "").toLowerCase();
      const city = (listing.city || "").toLowerCase();
      const state = (listing.state || "").toLowerCase();

      return [id, address, city, state].some((field) =>
        field.includes(q)
      );
    });
  }, [safeListings, searchQuery]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredListings.length / itemsPerPage)),
    [filteredListings.length, itemsPerPage]
  );

  const pageRows = useMemo(
    () =>
      filteredListings.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      ),
    [filteredListings, currentPage]
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
    if (totalPages === 0 && currentPage !== 1) setCurrentPage(1);
  }, [currentPage, totalPages]);

  // When search changes, go back to page 1
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    if (uid) {
      pageRows.forEach((listing) => {
        const propertyId = listing.propertyId || listing.id;
        if (propertyId && !propertyLocksMap[propertyId] && !locksLoadingMap[propertyId]) {
        }

        // Fetch manager count independently
        if (propertyId && managersCountMap[propertyId] === undefined) {
          getAuth(app).currentUser?.getIdToken().then((token) => {
            fetch(
              `${process.env.REACT_APP_SEAM_SERVER_URL}/api/manager-access/${propertyId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            )
              .then((r) => r.json())
              .then((data) => {
                setManagersCountMap((prev) => ({
                  ...prev,
                  [propertyId]: (data.managers || []).length,
                }));
              })
              .catch(() => {
                setManagersCountMap((prev) => ({ ...prev, [propertyId]: 0 }));
              });
          });
        }
      });
    }
  }, [pageRows, uid, propertyLocksMap, locksLoadingMap, managersCountMap]);

  // Fetch locks for properties (mobile cards show Generate Access Code; fetch for both views so data is ready)
  useEffect(() => {
    if (uid) {
      pageRows.forEach((listing) => {
        const propertyId = listing.propertyId || listing.id;
        if (propertyId && !propertyLocksMap[propertyId] && !locksLoadingMap[propertyId]) {
          setLocksLoadingMap(prev => ({ ...prev, [propertyId]: true }));
          getLocksByProperty(propertyId)
            .then((locks) => {
              setPropertyLocksMap(prev => ({ ...prev, [propertyId]: locks || [] }));
              setLocksLoadingMap(prev => ({ ...prev, [propertyId]: false }));
            })
            .catch((err) => {
              console.error(`Error fetching locks for property ${propertyId}:`, err);
              setPropertyLocksMap(prev => ({ ...prev, [propertyId]: [] }));
              setLocksLoadingMap(prev => ({ ...prev, [propertyId]: false }));
            });
        }
      });
    }
  }, [pageRows, uid, locksLoadingMap, propertyLocksMap]);

  // Handle Generate Access Code button click
  const handleGenerateAccessCode = (e, listing) => {
    e.stopPropagation();
    const propertyId = listing.propertyId || listing.id;
    const locks = propertyLocksMap[propertyId] || [];

    if (locks.length === 0) {
      openSnack("No locks connected to this property. Please add a smart lock first.", "warning");
      return;
    }

    // If only one lock, use it directly; otherwise, let user select
    if (locks.length === 1) {
      setSelectedPropertyForAccessCode(listing);
      setSelectedLockIdForAccessCode(locks[0].lockId);
      setAccessCodeDialogOpen(true);
    } else {
      setSelectedPropertyForAccessCode(listing);
      setSelectedLockIdForAccessCode(locks[0].lockId);
      setAccessCodeDialogOpen(true);
    }
  };
  const handleFindMeATenantAgreed = () => {};
  const [smartPackageOpen, setSmartPackageOpen] = useState(false);

  const handleViewManagers = async (e, listingId) => {
    e.stopPropagation();
    setManagersListingId(listingId);
    setManagersLoading(true);
    setManagersDialogOpen(true);
    try {
      const auth = getAuth(app);
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(
        `${process.env.REACT_APP_SEAM_SERVER_URL}/api/manager-access/${listingId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      const managers = data.managers || [];
      setManagersCountMap((prev) => ({
        ...prev,
        [listingId]: managers.length,
      }));
      setSelectedListingManagers(managers);
    } catch (err) {
      openSnack("Failed to load managers", "error");
    } finally {
      setManagersLoading(false);
    }
  };

  const handleRevokeManager = async (listingId, managerUid) => {
    setRevokingManagerId(managerUid);
    try {
      const auth = getAuth(app);
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(
        `${process.env.REACT_APP_SEAM_SERVER_URL}/api/manager-access/${listingId}/${managerUid}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Failed to revoke");
      setSelectedListingManagers(prev => prev.filter(m => m.managerUid !== managerUid));
      openSnack("Manager access revoked", "success");
      setManagersCountMap((prev) => ({
        ...prev,
        [listingId]: Math.max(0, (prev[listingId] ?? 1) - 1),
      }));
    } catch (err) {
      openSnack(err.message || "Failed to revoke manager access", "error");
    } finally {
      setRevokingManagerId(null);
    }
  };

  const totalCount = filteredListings.length;
  const overallCount = safeListings.length;

  const startIdx = totalCount ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endIdx = totalCount
    ? Math.min(currentPage * itemsPerPage, totalCount)
    : 0;

  const isSelected = (id) => selectedIds.has(id);
  const toggleSelectOne = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const allPageSelected =
    pageRows.length > 0 &&
    pageRows.every((r) => !(r.ownerId === uid || r.ownerUid === uid) || selectedIds.has(r.propertyId));
  const somePageSelected =
    pageRows.length > 0 &&
    pageRows.some((r) => (r.ownerId === uid || r.ownerUid === uid) && selectedIds.has(r.propertyId)) &&
    !allPageSelected;

  const toggleSelectPage = (checked) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      pageRows.forEach((r) => {
        const isOwner = r.ownerId === uid || r.ownerUid === uid;
        if (isOwner) {
          checked ? next.add(r.propertyId) : next.delete(r.propertyId);
        }
      });
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const handleCopyLink = (id) => {
    navigator.clipboard
      .writeText(`${window.location.origin}/listing/${id}`)
      .then(() => openSnack("Listing URL copied to clipboard!", "success"))
      .catch(() => openSnack("Failed to copy URL", "error"));
  };

  const handleGenerateVendorLink = (id) => {
    const listing = listings.find(l => (l.id || l.propertyId) === id);
    if (listing) {
      setSelectedPropertyForVendorLink({
        id,
        address: listing.address || "Property",
        city: listing.city || "",
        state: listing.state || "",
      });
      setVendorLinkDialogOpen(true);
    }
  };

  const openTransferFor = (ids) => {
    setPendingIds(Array.isArray(ids) ? ids : [ids]);
    setPendingManagerIds(Array.isArray(ids) ? ids : [ids]);
    setTransferTypeOpen(true);
  };

  // Called AFTER successful payment; uses transferEmail (recipient)
  const confirmTransfer = async () => {
    try {
      const res = await transferListings(transferEmail, pendingIds);

      // Clear managersCountMap for transferred listings optimistically
      setManagersCountMap((prev) => {
        const next = { ...prev };
        pendingIds.forEach((id) => { next[id] = 0; });
        return next;
      });

      openSnack(
        `Transferred ${res.count} listing${res.count > 1 ? "s" : ""} to ${transferEmail}`,
        "success"
      );
      onTransfer?.(transferEmail, pendingIds);      
      if (pendingIds.length > 1) clearSelection();
    } catch {
    }
  };
  const continueToPaymentOrSkip = async () => {
    if (!isValidEmail(transferEmail)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    if (!uid) {
      openSnack("You must be logged in to pay for transfers.", "error");
      return;
    }

    const { skip } = await shouldSkipPayment(uid);
    setTransferOpen(false);
    if (skip) {
      openSnack("Coupon applied — no charge for this transfer.", "success");
      await confirmTransfer();
    } else {
      setPaymentOpen(true);
    }
  };

  // grant manager access
  const handleGrantManagerAccess = async () => {
    if (!isValidEmail(managerEmail)) {
      setManagerEmailError("Please enter a valid email address");
      return;
    }
    setManagerAccessLoading(true);
    try {
      const auth = getAuth(app);
      const fbUser = auth.currentUser;
      if (!fbUser) throw new Error("Not authenticated");
      const token = await fbUser.getIdToken();
      await Promise.all(
        pendingManagerIds.map((listingId) => {
          const locks = propertyLocksMap[listingId] || [];
          return fetch(`${process.env.REACT_APP_SEAM_SERVER_URL}/api/manager-access`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              listingId,
              managerEmail,
              locks,
              grantedBy: uid,
            }),
          }).then(async (r) => {
            if (!r.ok) {
              const body = await r.json().catch(() => ({}));
              throw new Error(body.error || "Failed to grant access");
            }
            return r.json();
          });
        })
      );
      openSnack(`Manager access granted to ${managerEmail}`, "success");
      setPendingManagerIds((ids) => {
        ids.forEach((id) => {
          setManagersCountMap((prev) => ({ ...prev, [id]: (prev[id] ?? 0) + 1 }));
        });
        return ids;
      });
      setManagerAccessOpen(false);
      setManagerEmail("");
    } catch (err) {
      openSnack(err.message || "Failed to grant manager access", "error");
    } finally {
      setManagerAccessLoading(false);
    }
  };

  /* ------------------------------ RENDER ------------------------------ */

  if (loading) return <Typography>Loading listings...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  const hasSearch = !!searchQuery.trim();

  if (selectedProperty) {
    return (
      <PropertyDetailView
        property={selectedProperty}
        onBack={() => setSelectedProperty(null)}
      />
    );
  }

  return (
    <Box sx={styles.container}>
      {/* Dashboard Title */}
      <Typography variant="h5" sx={styles.headerTitle}>
        {isRoleReady ? dashboardTitle : "Dashboard"}
      </Typography>

      {/* Header actions / counts */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={1.5}
        sx={styles.headerBar}
      >
        {/* Left: Add button */}
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={handleCreateListing}
          sx={{ alignSelf: { xs: "stretch", sm: "center" } }}
        >
          Add New Property
        </Button>

        <FindMeATenantButton
          uid={uid}
          email={payerEmail}
          ownerName={ownerName}
          onAgreed={handleFindMeATenantAgreed}
          sx={{ alignSelf: { xs: "stretch", sm: "center" } }}
        />

        <Button
          variant="outlined"
          color="primary"
          startIcon={<DevicesOtherOutlinedIcon />}
          onClick={() => setSmartPackageOpen(true)}
          sx={{ alignSelf: { xs: "stretch", sm: "center" } }}
        >
          New Smart Package
        </Button>

        <NewSmartPackageFlow
          open={smartPackageOpen}
          onClose={() => setSmartPackageOpen(false)}
          uid={uid}
          email={payerEmail}
          fullName={ownerName}
        />


        {/* Middle: Search */}
        <TextField
          size="small"
          placeholder="Search by address, city or ID"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            width: { xs: "100%", sm: 260, md: 320 },
            "& .MuiInputBase-root": {
              fontSize: { xs: "0.875rem", sm: "1rem" },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            "aria-label": "Search listings",
          }}
        />

        {/* Right: counts */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: { xs: "left", sm: "right" },
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {totalCount > 0
            ? `Showing ${startIdx}–${endIdx} of ${totalCount} listing${totalCount !== 1 ? "s" : ""
            }${hasSearch && overallCount !== totalCount
              ? ` (filtered from ${overallCount})`
              : ""
            }`
            : hasSearch
              ? `No listings match your search${overallCount ? ` (out of ${overallCount} total)` : ""
              }.`
              : `No listings.`}
        </Typography>
      </Stack>

      {/* Selection toolbar */}
      {selectedIds.size > 0 && (
        <Toolbar sx={styles.toolbar}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 1, sm: 1.5 }}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ width: "100%" }}
          >
            <Chip
              label={`${selectedIds.size} selected`}
              size="small"
              sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
            />
            <Divider
              flexItem
              orientation={{ xs: "horizontal", sm: "vertical" }}
              sx={{ mx: { xs: 0, sm: 1 }, my: { xs: 1, sm: 0 } }}
            />
            <Button
              variant="contained"
              startIcon={<SwapHoriz />}
              onClick={() => openTransferFor(Array.from(selectedIds))}
              size="small"
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Transfer Selected
            </Button>
            <Button
              variant="text"
              onClick={clearSelection}
              size="small"
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Clear
            </Button>
          </Stack>
        </Toolbar>
      )}

      {/* Desktop / Tablet (md+) — Table */}
      {isMdUp ? (
        totalCount ? (
          <>
            <Table sx={styles.table.root} aria-label="Listings table">
              <TableHead>
                <TableRow>
                  <TableCell width={56} padding="checkbox">
                    <Checkbox
                      indeterminate={somePageSelected}
                      checked={allPageSelected}
                      onChange={(e) => toggleSelectPage(e.target.checked)}
                      inputProps={{ "aria-label": "Select visible listings" }}
                    />
                  </TableCell>
                  <TableCell>Property</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((listing) => {
                  const id = listing.propertyId;
                  const selected = isSelected(id);
                  const firstImage =
                    Array.isArray(listing.images) && listing.images.length
                      ? listing.images[0]
                      : null;
                  const isOwner = listing.ownerId === uid || listing.ownerUid === uid;

                  return (
                    <TableRow
                      key={id}
                      hover
                      selected={selected}
                      sx={{ ...styles.table.selectedRow, cursor: 'pointer' }}
                      onClick={() => setSelectedProperty(listing)}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        {isOwner && (
                          <Checkbox
                            checked={selected}
                            onChange={() => toggleSelectOne(id)}
                            inputProps={{ "aria-label": `Select listing ${id}` }}
                          />
                        )}
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
                          {firstImage && (
                            <img
                              src={firstImage}
                              alt=""
                              loading="lazy"
                              width={80}
                              height={80}
                              style={styles.table.image}
                            />
                          )}
                          <Box minWidth={0}>
                            <Typography variant="subtitle2" noWrap title={listing.address || "No Address"}>
                              {listing.address || "No Address"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap title={id}>
                              ID: {id}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell>{`${listing.address || "N/A"}, ${listing.city || "N/A"
                        }, ${listing.state || "N/A"}`}</TableCell>
                      <TableCell>{`$${listing.rent || "N/A"}`}</TableCell>

                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                          alignItems="center"
                          flexWrap="wrap"
                          sx={{ gap: { xs: 0.5, sm: 1 } }}
                        >
                          {/* Primary Actions Group */}
                          <Box sx={{
                            display: "flex",
                            gap: 0.5,
                            alignItems: "center",
                            borderRight: isOwner ? ((theme) => `1px solid ${theme.palette.divider}`) : 'none',
                            pr: isOwner ? 1 : 0,
                            mr: isOwner ? 0.5 : 0,
                          }}>
                            <Tooltip title="Preview">
                              <IconButton
                                color="primary"
                                onClick={() => handleViewListing(id)}
                                aria-label={`Preview listing ${id}`}
                                size="small"
                                sx={{
                                  fontSize: { xs: "1rem", sm: "1.25rem" },
                                  "&:hover": { bgcolor: "primary.light", color: "white" },
                                }}
                              >
                                <Visibility fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                            {isOwner && (
                              <Tooltip title="Edit">
                                <IconButton
                                  color="primary"
                                  onClick={() => handleEditListing(id)}
                                  aria-label={`Edit listing ${id}`}
                                  size="small"
                                  sx={{
                                    fontSize: { xs: "1rem", sm: "1.25rem" },
                                    "&:hover": { bgcolor: "primary.light", color: "white" },
                                  }}
                                >
                                  <Edit fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {isOwner && (
                              <Tooltip title="Delete">
                                <IconButton
                                  color="error"
                                  onClick={() => handleDeleteListing(id)}
                                  aria-label={`Delete listing ${id}`}
                                  size="small"
                                  sx={{
                                    fontSize: { xs: "1rem", sm: "1.25rem" },
                                    "&:hover": { bgcolor: "error.light", color: "white" },
                                  }}
                                >
                                  <Delete fontSize="inherit" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>

                          {/* Secondary Actions Group */}
                          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                            <Tooltip title="Copy Public URL">
                              <IconButton
                                color="info"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyLink(id);
                                }}
                                aria-label={`Copy public URL for ${id}`}
                                size="small"
                                sx={{
                                  fontSize: { xs: "1rem", sm: "1.25rem" },
                                  "&:hover": { bgcolor: "info.light", color: "white" },
                                }}
                              >
                                <LinkIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Generate Maintenance Vendor Link">
                              <IconButton
                                color="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleGenerateVendorLink(id);
                                }}
                                aria-label={`Generate vendor link for ${id}`}
                                size="small"
                                sx={{
                                  fontSize: { xs: "1rem", sm: "1.25rem" },
                                  "&:hover": { bgcolor: "secondary.light", color: "white" },
                                }}
                              >
                                <Business fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                            {isOwner && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<SwapHoriz />}
                                onClick={() => openTransferFor([id])}
                                sx={{
                                  fontSize: { xs: "0.7rem", sm: "0.875rem" },
                                  px: { xs: 1, sm: 1.5 },
                                  ml: 0.5,
                                }}
                              >
                                Transfer
                              </Button>
                            )}
                            {isOwner && managersCountMap[id] > 0 && (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={(e) => handleViewManagers(e, id)}
                                sx={{
                                  fontSize: { xs: "0.7rem", sm: "0.875rem" },
                                  px: { xs: 1, sm: 1.5 },
                                  ml: 0.5,
                                }}
                              >
                                Managers
                              </Button>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            <Box sx={styles.paginationBox}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, v) => setCurrentPage(v)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        ) : hasSearch ? (
          <Typography>
            No listings match your search. Try adjusting your keywords.
          </Typography>
        ) : (
          <Typography>No listings found. Start by creating a new listing.</Typography>
        )
      ) : (
        /* Mobile (xs–sm) — Cards */
        <>
          {totalCount ? (
            <>
              <Stack spacing={1.5}>
                {pageRows.map((listing) => {
                  const id = listing.propertyId;
                  const selected = isSelected(id);
                  const firstImage =
                    Array.isArray(listing.images) && listing.images.length
                      ? listing.images[0]
                      : null;
                  const isOwner = listing.ownerId === uid || listing.ownerUid === uid;

                  return (
                    <Box
                      key={id}
                      sx={{ ...styles.card.root, cursor: 'pointer' }}
                      aria-label={`Property ${listing.address || "No Address"}`}
                      onClick={() => setSelectedProperty(listing)}
                    >
                      {/* Mobile Card Layout: Photo and Address at Top */}
                      <Box sx={{ mb: 1.5 }}>
                        {/* Main Photo */}
                        <Box sx={{ position: "relative", mb: 1 }}>
                          {firstImage ? (
                            <img
                              src={firstImage}
                              alt={listing.address || "Property"}
                              loading="lazy"
                              style={{
                                width: "100%",
                                height: 200,
                                objectFit: "cover",
                                borderRadius: 8,
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: "100%",
                                height: 200,
                                bgcolor: "action.hover",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 14,
                                color: "text.secondary",
                                borderRadius: 8,
                              }}
                            >
                              No Image
                            </Box>
                          )}
                          {isOwner && (
                            <Checkbox
                              checked={selected}
                              onChange={(e) => {
                                e.stopPropagation();
                                toggleSelectOne(id);
                              }}
                              inputProps={{ "aria-label": `Select property ${id}` }}
                              sx={{
                                position: "absolute",
                                top: 8,
                                left: 8,
                                bgcolor: "background.paper",
                                borderRadius: 2,
                              }}
                            />
                          )}
                        </Box>

                        {/* Address at Top */}
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, mb: 0.5 }}
                          title={listing.address || "No Address"}
                        >
                          {listing.address || "No Address"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {`${listing.city || "N/A"}, ${listing.state || "N/A"}`}
                        </Typography>
                      </Box>

                      {/* Content */}
                      <Box minWidth={0}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                          <Typography
                            variant="caption"
                            sx={styles.card.idText}
                            title={id}
                          >
                            ID: {id}
                          </Typography>
                          <Chip
                            size="small"
                            label={`$${listing.rent || "N/A"}`}
                            variant="outlined"
                          />
                        </Box>

                        {/* Connected Locks Display */}
                        {(() => {
                          const locks = propertyLocksMap[id] || [];
                          const isLoading = locksLoadingMap[id];

                          if (isLoading) {
                            return (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                <CircularProgress size={16} />
                                <Typography variant="caption" color="text.secondary">
                                  Loading locks...
                                </Typography>
                              </Box>
                            );
                          }

                          if (locks.length > 0) {
                            return (
                              <Box sx={{ mb: 1.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                                  Connected Locks ({locks.length})
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.5}>
                                  {locks.map((lock) => (
                                    <Chip
                                      key={lock.lockId}
                                      icon={
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                          {lock.isLocked !== undefined ? (
                                            lock.isLocked ? (
                                              <Lock sx={{ fontSize: 14 }} />
                                            ) : (
                                              <LockOpen sx={{ fontSize: 14 }} />
                                            )
                                          ) : (
                                            <Lock sx={{ fontSize: 14 }} />
                                          )}
                                          {lock.isOnline !== undefined && (
                                            <FiberManualRecord
                                              sx={{
                                                fontSize: 8,
                                                color: lock.isOnline ? "#81c241" : "#333333",
                                              }}
                                            />
                                          )}
                                        </Box>
                                      }
                                      label={
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                          <Typography variant="caption" noWrap sx={{ maxWidth: 100 }}>
                                            {lock.deviceName || "Lock"}
                                          </Typography>
                                          {lock.batteryLevel !== undefined && lock.batteryLevel !== null && (
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                              {(lock.batteryLevel > 20 || lock.batteryLevel > 0.2) ? (
                                                <BatteryFull sx={{ fontSize: 12, color: "#81c241" }} />
                                              ) : (
                                                <BatteryAlert sx={{ fontSize: 12, color: "#666666" }} />
                                              )}
                                              <Typography variant="caption" sx={{ ml: 0.25 }}>
                                                {lock.batteryLevel <= 1 ? Math.round(lock.batteryLevel * 100) : Math.round(lock.batteryLevel)}%
                                              </Typography>
                                            </Box>
                                          )}
                                        </Box>
                                      }
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: "0.7rem" }}
                                    />
                                  ))}
                                </Stack>
                              </Box>
                            );
                          }

                          return (
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
                              No locks connected
                            </Typography>
                          );
                        })()}

                        {/* Generate Access Code Button */}
                        <Button
                          variant="contained"
                          size="small"
                          fullWidth
                          onClick={(e) => handleGenerateAccessCode(e, listing)}
                          sx={{ mb: 2 }}
                          disabled={!propertyLocksMap[id] || propertyLocksMap[id].length === 0}
                        >
                          Generate Access Code
                        </Button>

                        {/* Primary Actions: Preview, Edit, Delete */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            mb: 1.5,
                            flexWrap: "wrap",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => handleViewListing(id)}
                            aria-label={`Preview listing ${id}`}
                            sx={{
                              flex: { xs: "1 1 auto", sm: "0 0 auto" },
                              minWidth: { xs: "calc(33.333% - 8px)", sm: 100 },
                            }}
                          >
                            Preview
                          </Button>
                          {isOwner && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              onClick={() => handleEditListing(id)}
                              startIcon={<Edit />}
                              aria-label={`Edit listing ${id}`}
                              sx={{
                                flex: { xs: "1 1 auto", sm: "0 0 auto" },
                                minWidth: { xs: "calc(33.333% - 8px)", sm: 100 },
                              }}
                            >
                              Edit
                            </Button>
                          )}
                          {isOwner && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleDeleteListing(id)}
                              startIcon={<Delete />}
                              aria-label={`Delete listing ${id}`}
                              sx={{
                                flex: { xs: "1 1 auto", sm: "0 0 auto" },
                                minWidth: { xs: "calc(33.333% - 8px)", sm: 100 },
                              }}
                            >
                              Delete
                            </Button>
                          )}
                        </Box>

                        {/* Secondary Actions: Copy Link, Vendor Link, Transfer */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Tooltip title="Copy Public URL">
                            <IconButton
                              color="info"
                              onClick={() => handleCopyLink(id)}
                              aria-label={`Copy public URL for ${id}`}
                              size="small"
                              sx={{
                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                              }}
                            >
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Generate Maintenance Vendor Link">
                            <IconButton
                              color="secondary"
                              onClick={() => handleGenerateVendorLink(id)}
                              aria-label={`Generate vendor link for ${id}`}
                              size="small"
                              sx={{
                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                              }}
                            >
                              <Business fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isOwner && (
                            <Button
                              size="small"
                              variant="text"
                              color="secondary"
                              startIcon={<SwapHoriz />}
                              onClick={() => openTransferFor([id])}
                              aria-label={`Transfer listing ${id}`}
                              sx={{
                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                              }}
                            >
                              Transfer
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>

              <Box sx={styles.paginationBox}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, v) => setCurrentPage(v)}
                  color="primary"
                />
              </Box>
            </>
          ) : hasSearch ? (
            <Typography>
              No listings match your search. Try adjusting your keywords.
            </Typography>
          ) : (
            <Typography>No listings found. Start by creating a new listing.</Typography>
          )}
        </>
      )}

      {/* Transfer Modal (collect RECIPIENT email only) */}
      <Dialog
        open={transferOpen}
        onClose={() => (xferLoading ? null : setTransferOpen(false))}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: styles.dialogPaper }}
      >
        <DialogTitle>
          Transfer {pendingIds.length > 1 ? "Listings" : "Listing"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {xferError && <Alert severity="error">{xferError.message}</Alert>}
            <Typography variant="body2" color="text.secondary">
              You are about to transfer <strong>{pendingIds.length}</strong>{" "}
              listing{pendingIds.length > 1 ? "s" : ""}.
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Estimated cost: ${priceForCount(pendingIds.length).toFixed(2)} (Base $
              {TRANSFER_BASE.toFixed(2)} + ${TRANSFER_EXTRA.toFixed(2)} per additional)
            </Typography>
            <TextField
              autoFocus
              label="Recipient Email"
              type="email"
              fullWidth
              placeholder="recipient@company.com"
              value={transferEmail}
              onChange={(e) => {
                setTransferEmail(e.target.value);
                if (emailError) setEmailError("");
              }}
              error={!!emailError}
              helperText={emailError || "The account to receive these listing(s)."}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  continueToPaymentOrSkip();
                }
              }}
              disabled={xferLoading}
              inputProps={{ "aria-label": "Recipient email address" }}
            />
            {!payerEmail && (
              <Alert severity="warning">
                You must be logged in to pay for transfers.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setTransferOpen(false)} disabled={xferLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SwapHoriz />}
            onClick={continueToPaymentOrSkip}
            disabled={xferLoading || !payerEmail}
          >
            Continue to Payment
          </Button>
        </DialogActions>
      </Dialog>


      {/* Transfer Type Choice Modal */}
      <Dialog
        open={transferTypeOpen}
        onClose={() => setTransferTypeOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>
          What would you like to do?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose an action for {pendingIds.length} selected propert{pendingIds.length > 1 ? "ies" : "y"}.
          </Typography>
          <Stack spacing={1.5}>
            <Button
              variant="outlined"
              fullWidth
              size="large"
              startIcon={<SwapHoriz />}
              onClick={() => {
                setTransferTypeOpen(false);
                setTransferEmail("");
                setEmailError("");
                setTransferOpen(true);
              }}
              sx={{
                justifyContent: "flex-start",
                borderRadius: 2,
                py: 1.5,
                px: 2,
                borderColor: "divider",
                "&:hover": { borderColor: "primary.main", bgcolor: "primary.50" },
              }}
            >
              <Box sx={{ textAlign: "left" }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Selling Property ?
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Permanently transfer ownership to another account
                </Typography>
              </Box>
            </Button>

            <Button
              variant="outlined"
              fullWidth
              size="large"
              disabled={pendingManagerIds.some((id) => (managersCountMap[id] ?? 0) > 0)}
              startIcon={<Business />}
              onClick={() => {
                setTransferTypeOpen(false);
                setManagerEmail("");
                setManagerEmailError("");
                setManagerAccessOpen(true);
              }}
              sx={{
                justifyContent: "flex-start",
                borderRadius: 2,
                py: 1.5,
                px: 2,
                borderColor: "divider",
                "&:hover": { borderColor: "secondary.main", bgcolor: "secondary.50" },
              }}
            >
              <Box sx={{ textAlign: "left" }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Add Manager Access
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Grant a property manager access to this listing and its locks. Revocable at any time.
                </Typography>
              </Box>
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTransferTypeOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Add Manager Access Modal */}
      <Dialog
        open={managerAccessOpen}
        onClose={() => (managerAccessLoading ? null : setManagerAccessOpen(false))}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: styles.dialogPaper }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Add Manager Access</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              The manager will be able to view property details and connected lock data for{" "}
              <strong>{pendingManagerIds.length}</strong> propert{pendingManagerIds.length > 1 ? "ies" : "y"}.
              You can revoke their access at any time.
            </Typography>

            {/* Property + Lock Summary */}
            <Box
              sx={{
                bgcolor: "action.hover",
                borderRadius: 2,
                p: 1.5,
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {pendingManagerIds.map((id) => {
                const listing = safeListings.find((l) => l.propertyId === id);
                const locks = propertyLocksMap[id] || [];
                return (
                  <Box key={id} sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={700} noWrap>
                      {listing?.address || id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {listing?.city}, {listing?.state} · ${listing?.rent}/mo
                    </Typography>
                    {locks.length > 0 ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                        {locks.map((lock) => (
                          <Chip
                            key={lock.lockId}
                            label={lock.deviceName || "Lock"}
                            size="small"
                            icon={<Lock sx={{ fontSize: 12 }} />}
                            variant="outlined"
                            sx={{ fontSize: "0.7rem" }}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.disabled">
                        No locks connected
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>

            <TextField
              autoFocus
              label="Manager Email"
              type="email"
              fullWidth
              placeholder="manager@company.com"
              value={managerEmail}
              onChange={(e) => {
                setManagerEmail(e.target.value);
                if (managerEmailError) setManagerEmailError("");
              }}
              error={!!managerEmailError}
              helperText={managerEmailError || "The property manager's account email."}
              disabled={managerAccessLoading}
              onKeyDown={(e) => { if (e.key === "Enter") handleGrantManagerAccess(); }}
              inputProps={{ "aria-label": "Manager email address" }}
            />

            <Alert severity="info" sx={{ borderRadius: 2 }}>
              The manager will have <strong>read access only</strong> to property details and lock status.
              They will not be able to transfer or delete the property.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setManagerAccessOpen(false)} disabled={managerAccessLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={managerAccessLoading ? <CircularProgress size={16} /> : <Business />}
            onClick={handleGrantManagerAccess}
            disabled={managerAccessLoading}
          >
            Grant Access
          </Button>
        </DialogActions>
      </Dialog>



      {/* Payment Modal for Transfers (payer = logged-in user) */}
      <TransferPaymentModal
        open={paymentOpen}
        handleClose={() => setPaymentOpen(false)}
        numListings={pendingIds.length}
        payerEmail={payerEmail}
        onSuccess={confirmTransfer}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} sx={{ width: "100%" }} onClose={closeSnack}>
          {snack.message}
        </Alert>
      </Snackbar>
      <Backdrop
        open={xferLoading}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1, flexDirection: "column", gap: 2 }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="body2" color="inherit">
          Transferring {pendingIds.length} listing{pendingIds.length !== 1 ? "s" : ""}...
        </Typography>
      </Backdrop>
      {/* Access Code Manager Dialog */}
      {selectedPropertyForAccessCode && selectedLockIdForAccessCode && (
        <AccessCodeManager
          isOpen={accessCodeDialogOpen}
          onClose={() => {
            setAccessCodeDialogOpen(false);
            setSelectedPropertyForAccessCode(null);
            setSelectedLockIdForAccessCode(null);
          }}
          uid={uid}
          lockId={selectedLockIdForAccessCode}
          lockName={
            propertyLocksMap[selectedPropertyForAccessCode.propertyId || selectedPropertyForAccessCode.id]?.find(
              (l) => l.lockId === selectedLockIdForAccessCode
            )?.deviceName || "Smart Lock"
          }
          initialAddress={
            selectedPropertyForAccessCode.address
              ? `${selectedPropertyForAccessCode.address}, ${selectedPropertyForAccessCode.city || ""}, ${selectedPropertyForAccessCode.state || ""}`
              : ""
          }
        />
      )}

      {/* Generate Vendor Link Dialog */}
      {selectedPropertyForVendorLink && (
        <GenerateVendorLinkDialog
          open={vendorLinkDialogOpen}
          onClose={() => {
            setVendorLinkDialogOpen(false);
            setSelectedPropertyForVendorLink(null);
          }}
          listingId={selectedPropertyForVendorLink.id}
          propertyAddress={
            selectedPropertyForVendorLink.address
              ? `${selectedPropertyForVendorLink.address}, ${selectedPropertyForVendorLink.city || ""}, ${selectedPropertyForVendorLink.state || ""}`
              : ""
          }
        />
      )}

      <Dialog
        open={managersDialogOpen}
        onClose={() => setManagersDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: styles.dialogPaper }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Managers with Access</DialogTitle>
        <DialogContent dividers>
          {managersLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          ) : selectedListingManagers.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
              No active managers for this property.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {selectedListingManagers.map((manager) => (
                <Box
                  key={manager.managerUid}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    border: (t) => `1px solid ${t.palette.divider}`,
                    borderRadius: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {manager.managerEmail}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Granted{" "}
                      {manager.grantedAt?.toDate
                        ? manager.grantedAt.toDate().toLocaleDateString()
                        : "recently"}
                      {" · "}
                      {manager.locks?.length || 0} lock{manager.locks?.length !== 1 ? "s" : ""}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={revokingManagerId === manager.managerUid}
                    startIcon={
                      revokingManagerId === manager.managerUid
                        ? <CircularProgress size={14} />
                        : <Delete />
                    }
                    onClick={() => handleRevokeManager(managersListingId, manager.managerUid)}
                  >
                    Revoke
                  </Button>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setManagersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageListings;